import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw, CheckCheck, X, Zap, TrendingDown, DollarSign, Users, Target, ListTodo, ShieldAlert, Mail, Bell, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  credits: Zap,
  revenue: DollarSign,
  sla: ShieldAlert,
  churn: TrendingDown,
  kpi: Target,
  tasks: ListTodo,
  general: AlertTriangle,
};

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500', dot: 'bg-red-500' },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', badge: 'bg-amber-500', dot: 'bg-amber-500' },
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500', dot: 'bg-blue-400' },
};

function requestPushPermission() {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function sendBrowserPush(alert) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(`Konnect: ${alert.title}`, {
      body: alert.message,
      icon: '/favicon.ico',
      tag: `smart-alert-${alert.category}`,
    });
  } catch { /* silent */ }
}

export function SmartAlertsDropdown() {
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ email_enabled: true, push_enabled: true });

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/smart-alerts`);
      setAlerts(data.alerts || []);
      setUnread(data.unread_count || 0);
    } catch {
      // Silent fail — alerts are supplementary
    }
  }, []);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/smart-alerts/preferences`);
      setPrefs({ email_enabled: data.email_enabled ?? true, push_enabled: data.push_enabled ?? true });
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchPrefs();
    const interval = setInterval(fetchAlerts, 120000);
    return () => clearInterval(interval);
  }, [fetchAlerts, fetchPrefs]);

  const togglePref = async (key) => {
    const newVal = !prefs[key];
    const updated = { ...prefs, [key]: newVal };
    setPrefs(updated);
    try {
      await axios.put(`${API}/api/smart-alerts/preferences`, { [key]: newVal });
      toast.success(`${key === 'email_enabled' ? 'Email' : 'Push'} alerts ${newVal ? 'enabled' : 'disabled'}`);
      if (key === 'push_enabled' && newVal) requestPushPermission();
    } catch {
      setPrefs(prefs); // rollback
      toast.error('Failed to update preference');
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/api/smart-alerts/analyze`, {});
      setAlerts(data.alerts || []);
      setUnread((data.alerts || []).filter(a => !a.read).length);
      if (data.cached) {
        toast.info('Showing cached results. Analysis runs every 5 minutes.');
      } else {
        const msgs = [];
        msgs.push(`AI analyzed ${Object.keys(data.metrics_snapshot || {}).length} metric categories`);
        if (data.email_sent) msgs.push('Email digest sent');
        toast.success(msgs.join(' · '));

        // Browser push for critical alerts
        if (prefs.push_enabled) {
          (data.alerts || [])
            .filter(a => a.severity === 'critical')
            .forEach(a => sendBrowserPush(a));
        }
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error('Insufficient AI credits. Purchase more to run analysis.');
      } else {
        toast.error('Failed to run alert analysis');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/api/smart-alerts/read`, {});
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const dismissAlert = async (idx) => {
    try {
      await axios.post(`${API}/api/smart-alerts/dismiss/${idx}`, {});
      setAlerts(prev => prev.filter((_, i) => i !== idx));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-gray-light hover:bg-white/10 relative"
          data-testid="smart-alerts-btn"
        >
          <Zap className={cn("h-5 w-5", unread > 0 && "text-amber-400")} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[380px] p-0 bg-[#1e293b] border-white/10" data-testid="smart-alerts-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-semibold text-white">Smart Alerts</span>
            {alerts.length > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5 bg-white/10 text-gray-300">{alerts.length}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-7 w-7", showPrefs ? "text-teal-400 bg-teal-500/10" : "text-gray-400 hover:text-white hover:bg-white/10")}
              onClick={() => setShowPrefs(!showPrefs)}
              data-testid="alert-settings-btn"
            >
              <Settings className="h-3.5 w-3.5" />
            </Button>
            {unread > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-gray-300 hover:text-white hover:bg-white/10" onClick={markAllRead}>
                <CheckCheck className="h-3 w-3 mr-1" /> Read
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2 text-teal-400 hover:text-teal-300 hover:bg-teal-500/10"
              onClick={runAnalysis}
              disabled={analyzing}
              data-testid="run-analysis-btn"
            >
              <RefreshCw className={cn("h-3 w-3 mr-1", analyzing && "animate-spin")} />
              {analyzing ? 'Analyzing...' : 'Analyze'}
            </Button>
          </div>
        </div>

        {/* Preferences Panel */}
        {showPrefs && (
          <div className="p-3 border-b border-white/10 bg-white/[0.03] space-y-2" data-testid="alert-prefs-panel">
            <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-2">Notification Delivery</p>
            <button
              onClick={() => togglePref('email_enabled')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
              data-testid="toggle-email-alerts"
            >
              <div className="flex items-center gap-2.5">
                <Mail className={cn("h-4 w-4", prefs.email_enabled ? "text-teal-400" : "text-gray-500")} />
                <div className="text-left">
                  <p className="text-sm text-white">Email Digest</p>
                  <p className="text-[10px] text-gray-400">Receive critical & warning alerts via email</p>
                </div>
              </div>
              <div className={cn("w-8 h-[18px] rounded-full transition-colors flex items-center px-0.5", prefs.email_enabled ? "bg-teal-500 justify-end" : "bg-gray-600 justify-start")}>
                <div className="w-3.5 h-3.5 rounded-full bg-white shadow-sm" />
              </div>
            </button>
            <button
              onClick={() => togglePref('push_enabled')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
              data-testid="toggle-push-alerts"
            >
              <div className="flex items-center gap-2.5">
                <Bell className={cn("h-4 w-4", prefs.push_enabled ? "text-teal-400" : "text-gray-500")} />
                <div className="text-left">
                  <p className="text-sm text-white">Browser Push</p>
                  <p className="text-[10px] text-gray-400">Desktop notifications for critical alerts</p>
                </div>
              </div>
              <div className={cn("w-8 h-[18px] rounded-full transition-colors flex items-center px-0.5", prefs.push_enabled ? "bg-teal-500 justify-end" : "bg-gray-600 justify-start")}>
                <div className="w-3.5 h-3.5 rounded-full bg-white shadow-sm" />
              </div>
            </button>
          </div>
        )}

        {/* Alerts List */}
        <div className="max-h-[400px] overflow-y-auto">
          {alerts.length > 0 ? (
            alerts.map((alert, idx) => {
              const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
              const Icon = CATEGORY_ICONS[alert.category] || CATEGORY_ICONS.general;
              return (
                <div
                  key={idx}
                  className={cn(
                    "p-3 border-b border-white/5 transition-colors hover:bg-white/5",
                    !alert.read && "bg-white/[0.03]"
                  )}
                  data-testid={`smart-alert-${idx}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5", colors.bg)}>
                      <Icon className={cn("h-4 w-4", colors.text)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={cn("text-xs font-semibold uppercase tracking-wider", colors.text)}>
                          {alert.severity}
                        </span>
                        {!alert.read && <span className={cn("w-1.5 h-1.5 rounded-full", colors.dot)} />}
                      </div>
                      <p className="text-sm font-medium text-white leading-tight">{alert.title}</p>
                      <p className="text-xs text-gray-400 mt-1 leading-relaxed">{alert.message}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-gray-500 hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); dismissAlert(idx); }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center mb-3">
                <Zap className="h-6 w-6 text-teal-400" />
              </div>
              <p className="text-sm font-medium text-white mb-1">No alerts yet</p>
              <p className="text-xs text-gray-400 mb-4">Click "Analyze" to run AI-powered diagnostics on your platform metrics</p>
              <Button
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8"
                onClick={runAnalysis}
                disabled={analyzing}
                data-testid="run-first-analysis-btn"
              >
                <RefreshCw className={cn("h-3 w-3 mr-1.5", analyzing && "animate-spin")} />
                {analyzing ? 'Analyzing...' : 'Run First Analysis'}
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        {alerts.length > 0 && (
          <div className="p-2 border-t border-white/10 text-center">
            <p className="text-[10px] text-gray-500">Powered by Kaharian AI Gateway</p>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
